// ========================================================================
// 💊 PRESCRIPTION PATIENT SERVICE - GESTIÓN COMPLETA DE PRESCRIPCIONES
// ========================================================================

import {
  API_BASE_PRESCRIPTION_PATIENT_URL,
  API_PRESCRIPTION_PATIENT_LIST,
  API_PRESCRIPTION_PATIENT_DETAILS,
  API_PRESCRIPTION_PATIENT_CREATE,
  API_PRESCRIPTION_PATIENT_UPDATE,
  API_PRESCRIPTION_PATIENT_DELETE
} from '../../../../helpers/urlHelper'; // Asegúrate de que la ruta sea correcta

// ========================================================================
// 🔧 CONFIGURACIÓN BASE
// ========================================================================

// Configuración base para todas las peticiones
const baseConfig = {
  headers: {
    'Content-Type': 'application/json',
  },
};

// Helper para manejar respuestas de la API
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP Error: ${response.status}`);
  }
  return response.json();
};

// Helper para construir URLs con parámetros
const buildUrl = (baseUrl, endpoint, id = null, queryParams = {}) => {
  let url = `${baseUrl}${endpoint}`;
  
  // Agregar ID si existe
  if (id) {
    url += `/${id}`;
  }
  
  // Agregar query parameters si existen
  const params = new URLSearchParams(queryParams);
  if (params.toString()) {
    url += `?${params.toString()}`;
  }
  
  return url;
};

// ========================================================================
// 🏥 PRESCRIPTION PATIENT SERVICE
// ========================================================================

export const prescriptionPatientService = {
  
  // ======================================================================
  // 📋 LISTAR PRESCRIPCIONES
  // ======================================================================
  
  /**
   * Obtiene la lista de todas las prescripciones
   * @param {Object} filters - Filtros de búsqueda (opcional)
   * @param {number} page - Número de página (opcional)
   * @param {number} limit - Límite de resultados por página (opcional)
   * @returns {Promise<Object>} Lista de prescripciones
   */
  getAll: async (filters = {}, page = 1, limit = 10) => {
    try {
      const queryParams = {
        ...filters,
        page,
        limit
      };
      
      const url = buildUrl(
        API_BASE_PRESCRIPTION_PATIENT_URL,
        API_PRESCRIPTION_PATIENT_LIST,
        null,
        queryParams
      );
      
      const response = await fetch(url, {
        method: 'GET',
        ...baseConfig,
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching prescriptions:', error);
      throw error;
    }
  },

  // ======================================================================
  // 🔍 OBTENER PRESCRIPCIÓN POR ID
  // ======================================================================
  
  /**
   * Obtiene una prescripción específica por su ID
   * @param {string|number} id - ID de la prescripción
   * @returns {Promise<Object>} Datos de la prescripción
   */
  getById: async (id) => {
    try {
      if (!id) {
        throw new Error('ID de prescripción es requerido');
      }
      
      const url = buildUrl(
        API_BASE_PRESCRIPTION_PATIENT_URL,
        API_PRESCRIPTION_PATIENT_DETAILS,
        id
      );
      
      const response = await fetch(url, {
        method: 'GET',
        ...baseConfig,
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error fetching prescription ${id}:`, error);
      throw error;
    }
  },

  // ======================================================================
  // ➕ CREAR NUEVA PRESCRIPCIÓN
  // ======================================================================
  
  /**
   * Crea una nueva prescripción
   * @param {Object} prescriptionData - Datos de la prescripción a crear
   * @returns {Promise<Object>} Prescripción creada
   */
  create: async (prescriptionData) => {
    try {
      if (!prescriptionData) {
        throw new Error('Datos de prescripción son requeridos');
      }
      
      const url = buildUrl(
        API_BASE_PRESCRIPTION_PATIENT_URL,
        API_PRESCRIPTION_PATIENT_CREATE
      );
      
      const response = await fetch(url, {
        method: 'POST',
        ...baseConfig,
        body: JSON.stringify(prescriptionData),
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating prescription:', error);
      throw error;
    }
  },

  // ======================================================================
  // ✏️ ACTUALIZAR PRESCRIPCIÓN
  // ======================================================================
  
  /**
   * Actualiza una prescripción existente
   * @param {string|number} id - ID de la prescripción a actualizar
   * @param {Object} prescriptionData - Datos actualizados de la prescripción
   * @returns {Promise<Object>} Prescripción actualizada
   */
  update: async (id, prescriptionData) => {
    try {
      if (!id) {
        throw new Error('ID de prescripción es requerido');
      }
      if (!prescriptionData) {
        throw new Error('Datos de prescripción son requeridos');
      }
      
      const url = buildUrl(
        API_BASE_PRESCRIPTION_PATIENT_URL,
        API_PRESCRIPTION_PATIENT_UPDATE,
        id
      );
      
      const response = await fetch(url, {
        method: 'PUT',
        ...baseConfig,
        body: JSON.stringify(prescriptionData),
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error updating prescription ${id}:`, error);
      throw error;
    }
  },

  // ======================================================================
  // 🗑️ ELIMINAR PRESCRIPCIÓN
  // ======================================================================
  
  /**
   * Elimina una prescripción
   * @param {string|number} id - ID de la prescripción a eliminar
   * @returns {Promise<Object>} Resultado de la eliminación
   */
  delete: async (id) => {
    try {
      if (!id) {
        throw new Error('ID de prescripción es requerido');
      }
      
      const url = buildUrl(
        API_BASE_PRESCRIPTION_PATIENT_URL,
        API_PRESCRIPTION_PATIENT_DELETE,
        id
      );
      
      const response = await fetch(url, {
        method: 'DELETE',
        ...baseConfig,
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error deleting prescription ${id}:`, error);
      throw error;
    }
  },

  // ======================================================================
  // 🔍 MÉTODOS AUXILIARES ESPECÍFICOS
  // ======================================================================
  
  /**
   * Busca prescripciones por paciente
   * @param {string|number} patientId - ID del paciente
   * @returns {Promise<Object>} Prescripciones del paciente
   */
  getByPatientId: async (patientId) => {
    return await prescriptionPatientService.getAll({ patientId });
  },

  /**
   * Busca prescripciones por médico
   * @param {string|number} doctorId - ID del médico
   * @returns {Promise<Object>} Prescripciones del médico
   */
  getByDoctorId: async (doctorId) => {
    return await prescriptionPatientService.getAll({ doctorId });
  },

  /**
   * Busca prescripciones por estado
   * @param {string} status - Estado de la prescripción (active, completed, cancelled, etc.)
   * @returns {Promise<Object>} Prescripciones filtradas por estado
   */
  getByStatus: async (status) => {
    return await prescriptionPatientService.getAll({ status });
  },

  /**
   * Busca prescripciones por medicamento
   * @param {string} medication - Nombre o ID del medicamento
   * @returns {Promise<Object>} Prescripciones del medicamento específico
   */
  getByMedication: async (medication) => {
    return await prescriptionPatientService.getAll({ medication });
  },

  /**
   * Busca prescripciones por rango de fechas
   * @param {string} startDate - Fecha de inicio (formato YYYY-MM-DD)
   * @param {string} endDate - Fecha de fin (formato YYYY-MM-DD)
   * @returns {Promise<Object>} Prescripciones en el rango de fechas
   */
  getByDateRange: async (startDate, endDate) => {
    return await prescriptionPatientService.getAll({ startDate, endDate });
  },

  // ======================================================================
  // 📊 MÉTODOS DE ESTADÍSTICAS
  // ======================================================================
  
  /**
   * Obtiene estadísticas de prescripciones
   * @returns {Promise<Object>} Estadísticas de prescripciones
   */
  getStats: async () => {
    try {
      const url = buildUrl(
        API_BASE_PRESCRIPTION_PATIENT_URL,
        API_PRESCRIPTION_PATIENT_LIST,
        null,
        { stats: true }
      );
      
      const response = await fetch(url, {
        method: 'GET',
        ...baseConfig,
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching prescription stats:', error);
      throw error;
    }
  }
};

// ========================================================================
// 📤 EXPORTACIÓN POR DEFECTO
// ========================================================================

export default prescriptionPatientService;